import './styles.scss';
import { appRoutes, projectsRoutes } from 'constants/navigation';
import {
  Project,
  ProjectModuleType,
  ProjectsDocument,
  ProjectsQuery,
  ProjectsQueryVariables,
  useCreateProjectMutation,
  useProjectCollaboratorsCreateManyMutation,
} from 'generated/graphql';
import { FC } from 'react';
import { useNavigate } from 'react-router-dom';
import { TNotification, useNotification } from '@spotted-zebra-uk/ui-components';
import ProjectOverviewForm, {
  IProjectOverviewFormSubmitValues,
  ProjectFormMode,
} from './components/ProjectOverviewForm';
import ProjectBar from './ProjectBar';

const ProjectCreate: FC = () => {
  const { handleMsgType } = useNotification();

  const navigate = useNavigate();

  const [createProjectCollaborators] =
    useProjectCollaboratorsCreateManyMutation({
      onError: () =>
        handleMsgType({
          type: TNotification.error,
          title: 'Something went wrong while saving role collaborators',
        }),
    });

  const [createProjectMutation] = useCreateProjectMutation({
    update(cache, { data }) {
      const project = data?.project as Project;
      if (project) {
        const data = cache.readQuery<ProjectsQuery, ProjectsQueryVariables>({
          query: ProjectsDocument,
        });
        if (data?.projects && project) {
          cache.writeQuery({
            query: ProjectsDocument,
            data: { projects: [project, ...data.projects] },
          });
        }
      }
    },
    onError: () => {
      return null;
    },
    onCompleted: async ({ project }) => {
      if (project) {
        handleMsgType({
          type: TNotification.success,
          title: 'Successfully created role',
        });
      }
    },
  });

  const handleCreateProject = async ({
    name,
    collaborators,
    companyId,
    requireSheetsExport,
    productSolution,
  }: IProjectOverviewFormSubmitValues) => {
    const result = await createProjectMutation({
      variables: {
        name,
        companyId: Number(companyId),
        moduleType: ProjectModuleType.Hiring,
        requireSheetsExport,
        productSolution,
      },
    });
    const projectId = result.data?.project.id;
    if (projectId) {
      if (collaborators.length) {
        await createProjectCollaborators({
          variables: {
            collaborators: collaborators.map((collaborator) => ({
              actorRole: collaborator.role,
              userId: collaborator.userId,
              groupId: collaborator.groupId,
              projectId: Number(projectId),
            })),
          },
        });
      }
      navigate(projectsRoutes.editProjectView.url(projectId));
    } else {
      handleMsgType({
        type: TNotification.error,
        title: 'Something has gone wrong while creating this project',
      });
    }
  };

  const handleCancel = () => {
    navigate(appRoutes.projects.url());
  };

  return (
    <div className="project">
      <div className="project-create">
        <ProjectBar />
        <div className="project-create__title">New role</div>
        <ProjectOverviewForm
          onProjectSave={handleCreateProject}
          onCancel={handleCancel}
          mode={ProjectFormMode.CREATE}
        />
      </div>
    </div>
  );
};

export default ProjectCreate;
